import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney } from '@lombok/shared';
import type { OrderStatus, OrderView } from '@lombok/shared';
import { Button, Card } from '@lombok/ui';
import { useCanCreateOrders } from '@/auth/useCanCreateOrders';
import { AccountName } from '@/components/AccountName';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill, statusLabel } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { useAllStatusList, useOrders } from '@/data/hooks';
import { api } from '@/lib/api';
import { orderLabel } from '@/lib/names';
import { isPriced } from '@/lib/orderView';
import { ORDER_STATUS_TABS, type OrderStatusTab } from './labels';
import { SortSelect } from '@/components/SortSelect';
import { byDate, byNumber, sortItems, thenBy, type SortOption } from '@/lib/sort';

const total = (o: OrderView) => (isPriced(o) ? o.totalMinorUsd : 0);

const ORDER_SORT: SortOption<OrderView>[] = [
  { value: 'recent', label: 'Más recientes', compare: byDate((o) => o.createdAt, 'desc') },
  { value: 'oldest', label: 'Más antiguos', compare: byDate((o) => o.createdAt) },
  { value: 'total_desc', label: 'Total mayor', compare: byNumber(total, 'desc') },
  { value: 'total_asc', label: 'Total menor', compare: byNumber(total) },
  {
    value: 'status',
    label: 'Estado',
    compare: thenBy<OrderView>(
      (a, b) => statusLabel('order', a.status).localeCompare(statusLabel('order', b.status), 'es'),
      byDate((o) => o.createdAt, 'desc'),
    ),
  },
];

interface OrdersListPageProps {
  /** Group root for detail links, e.g. "/app/admin/pedidos". */
  basePath: string;
  title?: string;
  description?: string;
  /** Optional "Crear pedido" action target (sales/admin only). */
  createPath?: string;
  /**
   * When true (admin/finance/warehouse), the backend rejects an unfiltered list, so it always
   * gets a concrete status. The full lifecycle still offers "Todos" (one query per status,
   * merged); a custom `statusTabs` set (warehouse queue) has no "Todos".
   */
  requireStatusFilter?: boolean;
  /**
   * Override the filter-tab set (e.g. the warehouse fulfillment queue shows only
   * the states it acts on). Defaults to the full lifecycle {@link ORDER_STATUS_TABS}.
   */
  statusTabs?: readonly OrderStatusTab[];
  /**
   * Initial selected status when `requireStatusFilter` is on (e.g. the Despachos
   * view opens on `dispatched`). Must be one of `statusTabs`; defaults to the
   * first tab.
   */
  defaultStatus?: OrderStatus;
  /** Show which customer each order is for (distributor portal: its sub-accounts). */
  showCustomer?: boolean;
}

/**
 * Pedidos list with status-filter tabs. Scoping is server-side (customer → own,
 * sales_rep → assigned, admin/finance → all but with a required filter). Money
 * via shared helpers. Responsive: table on desktop, cards on mobile.
 */
export function OrdersListPage({
  basePath,
  title = 'Pedidos',
  description = 'Pedidos y su estado.',
  createPath,
  requireStatusFilter = false,
  statusTabs = ORDER_STATUS_TABS,
  defaultStatus,
  showCustomer = false,
}: OrdersListPageProps) {
  // "Todos" exists unless a custom status set (warehouse queue) replaces the lifecycle tabs.
  const allTab = !requireStatusFilter || statusTabs === ORDER_STATUS_TABS;
  // Default tab: an explicit default; else "Todos" when it exists; else the first status.
  const [status, setStatus] = useState<OrderStatus | undefined>(
    defaultStatus ?? (allTab ? undefined : statusTabs[0]!.status),
  );

  // The server refuses an unfiltered list for admin/finance, so their "Todos" merges one query per status.
  const mergeAll = requireStatusFilter && status === undefined;
  const single = useOrders({ status, limit: 100 }, !mergeAll);
  const merged = useAllStatusList({
    scope: 'orders',
    statuses: statusTabs.flatMap((t) => (t.status ? [t.status] : [])),
    fetchStatus: (s) => api.listOrders({ status: s, limit: 100 }),
    sortDate: (o) => o.createdAt,
    enabled: mergeAll,
  });
  const query = mergeAll
    ? { isLoading: merged.isLoading, isError: merged.isError, error: merged.error, refetch: merged.refetch }
    : single;
  const [sort, setSort] = useState('recent');
  const rawItems = mergeAll ? merged.items : (single.data?.items ?? []);
  const items = sortItems(rawItems, ORDER_SORT, sort);
  // Warehouse gets no prices: no total column and no way to sort by it.
  const showTotal = rawItems.every(isPriced);

  const canCreate = useCanCreateOrders();
  const tabs = useMemo(
    () => (allTab ? [{ label: 'Todos', status: undefined }, ...statusTabs] : statusTabs),
    [allTab, statusTabs],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          createPath && canCreate ? (
            <Link to={createPath}>
              <Button size="sm">Crear pedido</Button>
            </Link>
          ) : undefined
        }
      />

      {/* Status filter tabs (horizontal scroll on mobile). */}
      <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="scrollbar-hover -mx-1 flex min-w-0 gap-2 overflow-x-auto px-1 pb-1">
        {tabs.map((tab) => {
          const active = tab.status === status;
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => setStatus(tab.status)}
              aria-pressed={active}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors ${
                active
                  ? 'border-brand-primary bg-brand-primary/10 font-medium text-brand-primary'
                  : 'border-border text-text-muted hover:border-border-strong hover:text-text-primary'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <SortSelect options={ORDER_SORT} value={sort} onChange={setSort} />
      </div>

      <Card className="overflow-hidden">
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={items.length === 0}
          loadingLabel="Cargando pedidos…"
          loadingVariant="table"
          skeletonCols={5}
          emptyTitle="No hay pedidos"
          emptyDescription="No se encontraron pedidos para este filtro."
        >
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-4 py-3 font-medium">Pedido</th>
                  {showCustomer ? <th className="px-4 py-3 font-medium">Cliente</th> : null}
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Artículos</th>
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  {showTotal ? <th className="px-4 py-3 text-right font-medium">Total (USD)</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.orderId} className="border-b border-border last:border-0 hover:bg-surface-muted">
                    <td className="px-4 py-3">
                      <Link to={`${basePath}/${o.orderId}`} className="font-mono text-brand-primary hover:underline">
                        {orderLabel(o)}
                      </Link>
                    </td>
                    {showCustomer ? (
                      <td className="px-4 py-3 text-text-primary">
                        <AccountName id={o.customerId} />
                      </td>
                    ) : null}
                    <td className="px-4 py-3">
                      <StatusPill kind="order" value={o.status} />
                    </td>
                    <td className="px-4 py-3 text-text-muted">{o.items.length}</td>
                    <td className="px-4 py-3 text-text-muted">{formatDate(o.createdAt)}</td>
                    {isPriced(o) ? (
                      <td className="px-4 py-3 text-right font-medium text-text-primary">
                        {formatMoney(o.totalMinorUsd)}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="divide-y divide-border md:hidden">
            {items.map((o) => (
              <li key={o.orderId}>
                <Link to={`${basePath}/${o.orderId}`} className="flex flex-col gap-2 p-4 active:bg-surface-muted">
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-mono text-sm text-brand-primary">{orderLabel(o)}</span>
                    <StatusPill kind="order" value={o.status} />
                  </div>
                  {showCustomer ? (
                    <p className="text-sm text-text-primary">
                      <AccountName id={o.customerId} />
                    </p>
                  ) : null}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      {o.items.length} artículo{o.items.length === 1 ? '' : 's'} · {formatDate(o.createdAt)}
                    </span>
                    {isPriced(o) ? (
                      <span className="font-semibold text-text-primary">{formatMoney(o.totalMinorUsd)}</span>
                    ) : null}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}

/** Compact local date (es-VE). Falls back to the raw value if unparseable. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
}
